/*
 * Очередь исходящих SMS / VK / фото отчёта для APK.
 * Хранение: IndexedDB. Flush при online / resume.
 */

import axios from 'axios';
import { sendSmsTo359 } from './installerSms';

const DB_NAME = 'installer_outbox_v1';
const STORE = 'jobs';
const DB_VERSION = 1;

let flushRunning = false;
let watcherStarted = false;
const listeners = new Set();

const openDb = () =>
    new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, {
                    keyPath: 'id'
                });
                store.createIndex('createdAt', 'createdAt');
                store.createIndex('type', 'type');
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });

const withStore = async (mode, fn) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        let result;
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error || new Error('IDB'));
        try {
            const maybe = fn(store);
            if (maybe && typeof maybe.then === 'function') {
                maybe
                    .then(value => {
                        result = value;
                    })
                    .catch(reject);
            } else {
                result = maybe;
            }
        } catch (error) {
            reject(error);
        }
    });
};

const uid = () =>
    `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export const isOnline = () =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false;

export const fileToBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            const base64 = result.includes(',')
                ? result.split(',')[1]
                : result;
            resolve(base64);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const base64ToBlob = (base64, mime) => {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], {
        type: mime || 'application/octet-stream'
    });
};

const notify = async () => {
    const jobs = await listOutboxJobs();
    listeners.forEach(fn => {
        try {
            fn(jobs);
        } catch (error) {
            console.warn('outbox listener', error);
        }
    });
};

export const subscribeOutbox = fn => {
    listeners.add(fn);
    listOutboxJobs().then(fn).catch(() => fn([]));
    return () => listeners.delete(fn);
};

export const listOutboxJobs = async () => {
    if (typeof indexedDB === 'undefined') {
        return [];
    }
    return withStore('readonly', store =>
        new Promise((resolve, reject) => {
            const req = store.getAll();
            req.onsuccess = () => {
                const rows = req.result || [];
                rows.sort(
                    (a, b) =>
                        (a.createdAt || 0) - (b.createdAt || 0)
                );
                resolve(rows);
            };
            req.onerror = () => reject(req.error);
        })
    );
};

export const getOutboxCount = async () => {
    const jobs = await listOutboxJobs();
    return jobs.length;
};

export const enqueueOutboxJob = async job => {
    const row = {
        id: job.id || uid(),
        type: job.type,
        payload: job.payload || {},
        createdAt: Date.now(),
        attempts: 0,
        lastError: null,
        status: 'pending'
    };

    await withStore('readwrite', store => {
        store.put(row);
    });
    await notify();
    return row;
};

const removeJob = async id => {
    await withStore('readwrite', store => {
        store.delete(id);
    });
};

const patchJob = async (id, patch) => {
    await withStore('readwrite', store =>
        new Promise((resolve, reject) => {
            const getReq = store.get(id);
            getReq.onsuccess = () => {
                const current = getReq.result;
                if (!current) {
                    resolve(null);
                    return;
                }
                const next = { ...current, ...patch };
                store.put(next);
                resolve(next);
            };
            getReq.onerror = () => reject(getReq.error);
        })
    );
};

const isNetworkError = error => {
    if (!error) return false;
    if (!isOnline()) return true;
    if (!error.response && error.request) return true;
    if (error.code === 'ERR_NETWORK') return true;
    const msg = String(error.message || '').toLowerCase();
    return (
        msg.includes('network') ||
        msg.includes('offline') ||
        msg.includes('failed to fetch') ||
        msg.includes('timeout')
    );
};

const processJob = async job => {
    if (job.type === 'sms') {
        await sendSmsTo359(job.payload?.text || '');
        return;
    }

    if (job.type === 'vk') {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Нет авторизации для VK');
        }
        await axios.post(
            '/api/installer/vk-workspace/send',
            { text: job.payload?.text || '' },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: 30000
            }
        );
        return;
    }

    if (job.type === 'report_photo') {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Нет авторизации для фото');
        }
        const {
            taskId,
            fileName,
            mime,
            base64,
            marker
        } = job.payload || {};
        if (!taskId || !base64) {
            throw new Error('Битая запись фото в очереди');
        }
        const blob = base64ToBlob(base64, mime);
        const body = new FormData();
        body.append(
            'photo',
            blob,
            fileName || 'photo.jpg'
        );
        body.append('marker', marker || 'otchet');
        await axios.post(
            `/api/installer/work/${taskId}/report/photo`,
            body,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000
            }
        );
        return;
    }

    if (
        job.type === 'tmc_peer_offer' ||
        job.type === 'tmc_peer_accept'
    ) {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Нет авторизации для ТМЦ');
        }
        await axios.post(
            '/api/tmc/my/transfers/sync',
            job.payload || {},
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: 30000
            }
        );
        return;
    }

    if (job.type === 'tmc_issue_accept') {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Нет авторизации для ТМЦ');
        }
        await axios.post(
            '/api/tmc/my/issues/accept',
            {
                code: job.payload?.code,
                qr: job.payload?.qr || job.payload?.code
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: 30000
            }
        );
        return;
    }

    throw new Error(`Неизвестный тип очереди: ${job.type}`);
};

export const flushOutbox = async () => {
    if (flushRunning) {
        return { flushed: 0, failed: 0, skipped: true };
    }
    if (typeof indexedDB === 'undefined') {
        return { flushed: 0, failed: 0 };
    }

    flushRunning = true;
    let flushed = 0;
    let failed = 0;

    try {
        const jobs = await listOutboxJobs();
        for (const job of jobs) {
            /*
             * SMS можно пробовать и без интернета.
             * VK/фото — только online.
             */
            if (
                job.type !== 'sms' &&
                !isOnline()
            ) {
                continue;
            }

            try {
                await processJob(job);
                await removeJob(job.id);
                flushed += 1;
            } catch (error) {
                failed += 1;
                await patchJob(job.id, {
                    attempts: (job.attempts || 0) + 1,
                    lastError:
                        error?.response?.data?.message ||
                        error?.message ||
                        'Ошибка отправки',
                    status: 'pending'
                });
                if (
                    job.type !== 'sms' &&
                    isNetworkError(error)
                ) {
                    break;
                }
            }
        }
    } finally {
        flushRunning = false;
        await notify();
    }

    return { flushed, failed };
};

export const enqueueSms = async text => {
    const body = String(text || '').trim();
    if (!body) throw new Error('Пустой текст SMS');

    try {
        await sendSmsTo359(body);
        return { queued: false, sent: true };
    } catch (error) {
        await enqueueOutboxJob({
            type: 'sms',
            payload: { text: body }
        });
        return {
            queued: true,
            sent: false,
            error:
                error?.message ||
                'SMS в очереди до повтора'
        };
    }
};

export const enqueueVk = async text => {
    const body = String(text || '').trim();
    if (!body) throw new Error('Пустой текст VK');

    if (!isOnline()) {
        await enqueueOutboxJob({
            type: 'vk',
            payload: { text: body }
        });
        return { queued: true, sent: false };
    }

    try {
        const token = localStorage.getItem('token');
        await axios.post(
            '/api/installer/vk-workspace/send',
            { text: body },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: 30000
            }
        );
        return { queued: false, sent: true };
    } catch (error) {
        if (isNetworkError(error)) {
            await enqueueOutboxJob({
                type: 'vk',
                payload: { text: body }
            });
            return {
                queued: true,
                sent: false,
                error:
                    error?.response?.data?.message ||
                    error?.message
            };
        }
        throw error;
    }
};

export const enqueueReportPhoto = async ({
    taskId,
    file,
    marker = 'otchet'
}) => {
    if (!taskId || !file) {
        throw new Error('Нет задачи или файла');
    }

    const tryUpload = async () => {
        const token = localStorage.getItem('token');
        const body = new FormData();
        body.append('photo', file);
        body.append('marker', marker || 'otchet');
        const { data } = await axios.post(
            `/api/installer/work/${taskId}/report/photo`,
            body,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000
            }
        );
        return data;
    };

    if (!isOnline()) {
        const base64 = await fileToBase64(file);
        await enqueueOutboxJob({
            type: 'report_photo',
            payload: {
                taskId,
                marker: marker || 'otchet',
                fileName: file.name || 'photo.jpg',
                mime: file.type || 'image/jpeg',
                base64
            }
        });
        return { queued: true, sent: false, data: null };
    }

    try {
        const data = await tryUpload();
        return { queued: false, sent: true, data };
    } catch (error) {
        if (isNetworkError(error)) {
            const base64 = await fileToBase64(file);
            await enqueueOutboxJob({
                type: 'report_photo',
                payload: {
                    taskId,
                    fileName: file.name || 'photo.jpg',
                    mime: file.type || 'image/jpeg',
                    base64
                }
            });
            return {
                queued: true,
                sent: false,
                data: null,
                error:
                    error?.response?.data?.message ||
                    error?.message
            };
        }
        throw error;
    }
};

const postOrQueue = async ({ type, payload, tryOnline }) => {
    if (isOnline()) {
        try {
            await tryOnline();
            return { queued: false, sent: true };
        } catch (error) {
            if (!isNetworkError(error)) {
                throw error;
            }
        }
    }

    await enqueueOutboxJob({ type, payload });
    return { queued: true, sent: false };
};

export const enqueueTmcPeerOffer = async payload =>
    postOrQueue({
        type: 'tmc_peer_offer',
        payload,
        tryOnline: () => processJob({ type: 'tmc_peer_offer', payload })
    });

export const enqueueTmcPeerAccept = async payload =>
    postOrQueue({
        type: 'tmc_peer_accept',
        payload,
        tryOnline: () => processJob({ type: 'tmc_peer_accept', payload })
    });

export const enqueueTmcIssueAccept = async ({ code, qr }) =>
    postOrQueue({
        type: 'tmc_issue_accept',
        payload: { code, qr: qr || code },
        tryOnline: () =>
            processJob({
                type: 'tmc_issue_accept',
                payload: { code, qr: qr || code }
            })
    });

export const startOfflineQueueWatcher = () => {
    if (watcherStarted || typeof window === 'undefined') {
        return;
    }
    watcherStarted = true;

    const tick = () => {
        flushOutbox().catch(error =>
            console.warn('outbox flush', error)
        );
    };

    window.addEventListener('online', tick);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            tick();
        }
    });

    setTimeout(tick, 1200);
    setInterval(tick, 30000);
};

export const outboxTypeLabel = type => {
    if (type === 'sms') return 'SMS 359';
    if (type === 'vk') return 'VK Workspace';
    if (type === 'report_photo') return 'Фото отчёта';
    if (type === 'tmc_peer_offer') return 'ТМЦ: передача QR';
    if (type === 'tmc_peer_accept') return 'ТМЦ: приём QR';
    if (type === 'tmc_issue_accept') return 'ТМЦ: приём со склада';
    return type;
};

export const outboxJobTitle = job => {
    const payload = job?.payload || {};
    if (job.type === 'report_photo') {
        return payload.fileName || 'фото';
    }
    if (job.type === 'sms' || job.type === 'vk') {
        return payload.text || '—';
    }
    if (job.type === 'tmc_issue_accept') {
        return `Код ${payload.code || '—'}`;
    }
    if (
        job.type === 'tmc_peer_offer' ||
        job.type === 'tmc_peer_accept'
    ) {
        const lines = payload.lines || [];
        const names = lines
            .map(line => line.name || line.n)
            .filter(Boolean)
            .slice(0, 3)
            .join(', ');
        return names || `Позиций: ${lines.length || 0}`;
    }
    return '—';
};
